import { EmptyState } from "@/components/EmptyState";
import { KpiCard } from "@/components/KpiCard";
import { Panel } from "@/components/Panel";
import { cn } from "@/lib/cn";
import { formatCurrency, formatNumber, formatPercentRaw } from "@/lib/format";
import type { GoogleAdsCampaign } from "@/lib/types";
import { usePayload } from "@/lib/usePayload";
import { getGoogleAdsCampaigns } from "@/lib/viewModeSelectors";

export function GoogleAds() {
  const { payload, isLoading, viewMode, monthLabel } = usePayload();
  if (!payload) return <EmptyState title="No data yet for this period" loading={isLoading} />;

  const campaigns = getGoogleAdsCampaigns(payload, viewMode);
  const summary = payload.google_ads_summary || {};
  const displayLabel = viewMode === "current_week" ? (payload.weekly_reporting?.week_label || "Current Week") : monthLabel;

  const total = summary.total_campaigns ?? campaigns.length;
  const active = summary.active_campaigns ?? campaigns.filter((c: any) => (c.status || "").toUpperCase() === "ENABLED").length;
  const paused = total - active;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold">Google Ads Campaigns</h1>
        <p className="text-sm text-slate-500 mt-0.5">Search campaign status — {displayLabel}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard accent="blue" label="Total Campaigns" value={formatNumber(total)} />
        <KpiCard accent="green" label="Active Campaigns" value={formatNumber(active)} />
        <KpiCard accent="amber" label="Paused" value={formatNumber(paused)} />
      </div>

      <Panel title="Campaign List" right={<span className="text-xs text-slate-500">All search campaigns</span>}>
        {campaigns.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">No campaigns reported.</div>
        ) : (
          <CampaignTable campaigns={campaigns} />
        )}
      </Panel>
    </div>
  );
}

function CampaignTable({ campaigns }: { campaigns: GoogleAdsCampaign[] }) {
  return (
    <div className="overflow-x-auto -mx-5">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left label-xs border-b border-border">
            <th className="font-medium px-5 py-2.5">Campaign Name</th>
            <th className="font-medium px-3 py-2.5">Type</th>
            <th className="font-medium px-3 py-2.5">Status</th>
            <th className="font-medium px-3 py-2.5 text-right">Budget</th>
            <th className="font-medium px-3 py-2.5 text-right">Impressions</th>
            <th className="font-medium px-3 py-2.5 text-right">Clicks</th>
            <th className="font-medium px-3 py-2.5 text-right">CTR</th>
            <th className="font-medium px-3 py-2.5 text-right">Avg CPC</th>
            <th className="font-medium px-3 py-2.5 text-right">Cost</th>
            <th className="font-medium px-5 py-2.5 text-right">Conversions</th>
          </tr>
        </thead>
        <tbody>
          {campaigns.map((c) => {
            const isActive = (c.status || "").toUpperCase() === "ENABLED";
            return (
              <tr key={c.campaign_id || c.campaign_name} className="border-b border-border last:border-0 hover:bg-slate-50/60">
                <td className="px-5 py-2.5 font-medium">{c.campaign_name}</td>
                <td className="px-3 py-2.5">
                  <span className="pill bg-slate-100 text-slate-700 border border-slate-200 text-[10px] tracking-wider">
                    {(c.channel_type || "SEARCH").toUpperCase()}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <span className="inline-flex items-center gap-1.5 text-xs">
                    <span className={cn("h-1.5 w-1.5 rounded-full", isActive ? "bg-green-500" : "bg-slate-400")} />
                    <span className={isActive ? "text-green-700" : "text-slate-500"}>
                      {isActive ? "Enabled" : (c.status || "Paused")}
                    </span>
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right numeric-value">
                  {c.budget ? formatCurrency(c.budget, { compact: true }) : "—"}
                </td>
                <td className="px-3 py-2.5 text-right numeric-value">
                  {c.impressions ? formatNumber(c.impressions) : "—"}
                </td>
                <td className="px-3 py-2.5 text-right numeric-value">
                  {c.clicks ? formatNumber(c.clicks) : "—"}
                </td>
                <td className="px-3 py-2.5 text-right numeric-value">
                  {c.ctr ? formatPercentRaw(c.ctr) : "—"}
                </td>
                <td className="px-3 py-2.5 text-right numeric-value">
                  {c.average_cpc ? formatCurrency(c.average_cpc, { compact: true }) : "—"}
                </td>
                <td className="px-3 py-2.5 text-right numeric-value">
                  {c.cost ? formatCurrency(c.cost, { compact: true }) : "—"}
                </td>
                <td className="px-5 py-2.5 text-right numeric-value">
                  {c.conversions ? formatNumber(c.conversions) : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
